import { useEffect, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router";
import { useSnackbar } from "notistack";
import { Box, ButtonBase, Link, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ChevronRightOutlined from "@mui/icons-material/ChevronRightOutlined";

import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { componentTokens } from "@accredo/fe-shared/theme/tokens/components.js";

import { ICONS_BY_NAME } from "./documentTypeIcons.js";

const TILE_SKELETON_COUNT = 6;

/**
 * The dashboard's document-type tiles: one per active DocumentType, in the
 * order the API returns them (unlike PortalDocumentsTable's tabs, which only
 * show a type with at least one document). Every label/icon/order comes
 * from GET /data/documents/type-counts' `document_types` - no frontend
 * constant stands in for it. A configured type absent from the counts still
 * gets a tile, showing 0, rather than being left out.
 */
export default function DocumentTypeTiles() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [typeCounts, setTypeCounts] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const isLoading = typeCounts === null;

  useEffect(() => {
    backendGet("/data/documents/type-counts", {
      label: "Dokumenttypen",
      enqueueSnackbar,
    }).then((response) => {
      if (response?.data) {
        setTypeCounts(response.data);
        setDocumentTypes(response.document_types ?? []);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ mb: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: theme.palette.text.secondary,
          }}
        >
          Dokumente nach Typ
        </Typography>
        <Link
          component={RouterLink}
          to="/dokumente"
          underline="hover"
          sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
        >
          Alle Dokumente
          <ChevronRightOutlined fontSize="small" />
        </Link>
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {isLoading
          ? Array.from({ length: TILE_SKELETON_COUNT }).map((_, index) => (
              <Skeleton
                key={index}
                data-testid="document-type-tile-skeleton"
                variant="rounded"
                sx={{
                  flex: "1 1 160px",
                  minWidth: 160,
                  height: 84,
                  borderRadius: theme.spacing(1),
                }}
              />
            ))
          : documentTypes.map((documentType) => {
              const Icon = ICONS_BY_NAME[documentType.icon] ?? null;

              return (
                <ButtonBase
                  key={documentType.slug}
                  data-testid={`document-type-tile-${documentType.slug}`}
                  onClick={() =>
                    navigate(`/dokumente?type=${documentType.slug}`)
                  }
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    textAlign: "left",
                    gap: 1.5,
                    flex: "1 1 160px",
                    minWidth: 160,
                    p: 2,
                    borderRadius: theme.spacing(1),
                    border: `1px solid ${theme.palette.grey[200]}`,
                    backgroundColor: theme.palette.background.paper,
                    "&:hover": {
                      backgroundColor:
                        componentTokens.dataTable.row.hoverBackground,
                      // The icon square has the hover colour itself, so an
                      // outline keeps it visible.
                      "& .type-icon": {
                        outline: `1px solid ${theme.palette.grey[100]}`,
                      },
                    },
                  }}
                >
                  <Box
                    className="type-icon"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: theme.spacing(1),
                      backgroundColor: theme.palette.grey[50],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {Icon && (
                      <Icon
                        fontSize="small"
                        sx={{ color: theme.palette.text.primary }}
                      />
                    )}
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {documentType.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {typeCounts?.[documentType.slug] ?? 0} Dokumente
                    </Typography>
                  </Box>
                </ButtonBase>
              );
            })}
      </Stack>
    </Box>
  );
}
